import { useCallback, useMemo } from 'react'

import { Card, Input, useToast } from '@circlefin/components'
import { useForm, y } from '@circlefin/form'
import { SegmentEvents, useSegment } from '@services/segment'
import { GraphQLErrorBoundary } from '@shared/components/errors'
import { FormGrid } from '@shared/components/form'
import {
  ManageableUsersDocument,
  useInviteUserMutation,
  useManageableUsersQuery,
  UserRoleGroupType,
} from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import { UsersForms } from '../../forms'

import { RoleNote } from './RoleNote/RoleNote'

import type { SelectListItem } from '@circlefin/components/lib/SelectList'

const emailSchema = y.string().required().email()

const schema = y.object({
  /**
   * First Name.
   */
  firstName: y.string().required(),
  /**
   * Last Name.
   */
  lastName: y.string().required(),
  /**
   * Email Address.
   */
  email: emailSchema,
  /**
   * Assigned Role.
   */
  userRole: UsersForms.Dropdown.UserRolesSchema.required(),
})

export type InviteUserFormValues = y.InferType<typeof schema>

export const InviteUserForm: React.FC = () => {
  const { t } = useTranslation('settings')
  const { track } = useSegment()
  const toast = useToast()

  const users = useManageableUsersQuery({
    fetchPolicy: 'cache-and-network',
  })

  // a server-side check in real time
  const existingEmails = useMemo(
    () => (users.data?.manageableUsers ?? []).map((u) => u.email),
    [users.data?.manageableUsers],
  )

  const [Form, { watch, formState, reset }] = useForm<InviteUserFormValues>({
    schema: schema.shape({
      email: emailSchema.notOneOf(existingEmails, {
        key: 'notOneOf.emailExists',
      }),
    }),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      userRole: UserRoleGroupType.ADMINISTRATOR,
    },
  })

  const handleRoleChange = useCallback(
    (newRole: SelectListItem<InviteUserFormValues['userRole']>) => {
      track(SegmentEvents.DropdownSelected, {
        props: {
          field_name: 'userRoles',
          value: newRole.value,
        },
      })
    },
    [track],
  )

  const [inviteUser, inviteUserVars] = useInviteUserMutation({
    // Refetch the users list after the mutation is completed for the verify existing email logic found in the Base.Form component
    refetchQueries: [
      {
        query: ManageableUsersDocument,
      },
    ],
    awaitRefetchQueries: true,
  })

  const handleOnSubmit = useCallback(
    async ({ firstName, lastName, email, userRole }: InviteUserFormValues) => {
      track(SegmentEvents.SendInviteClicked)

      try {
        const { data } = await inviteUser({
          variables: {
            invitee: {
              firstName,
              lastName,
              email,
              permissionGroup: userRole,
            },
          },
        })

        toast.success(
          t(
            data?.inviteUser.requiredApproval === true
              ? 'inviteUser.success.withAdminApproval'
              : 'inviteUser.success.withoutAdminApproval',
            {
              email,
            },
          ),
        )

        track(SegmentEvents.InviteSent)
        reset()
      } catch {
        // Error is handled in the graphql error boundary
      }
    },
    [inviteUser, reset, t, toast, track],
  )

  const error = useMemo(
    () => inviteUserVars.error ?? users.error,
    [inviteUserVars.error, users.error],
  )

  const handleRetry = useCallback(() => {
    if (inviteUserVars.error) {
      inviteUserVars.reset()
    }

    if (users.error) {
      void users.refetch()
    }
  }, [inviteUserVars, users])

  return (
    <GraphQLErrorBoundary error={error} retry={handleRetry}>
      <Form onSubmit={handleOnSubmit}>
        <Card>
          <Card.Content className="px-12 py-0">
            <FormGrid className="pb-12 pt-6">
              <FormGrid.Element>
                <Form.Input
                  data-testid="invite-user-first-name"
                  disabled={formState.isSubmitting}
                  label={t('inviteUser.firstName')}
                  name="firstName"
                  placeholder={t('inviteUser.firstName')}
                />
              </FormGrid.Element>
              <FormGrid.Element>
                <Form.Input
                  data-testid="invite-user-last-name"
                  label={t('inviteUser.lastName')}
                  name="lastName"
                  placeholder={t('inviteUser.lastName')}
                />
              </FormGrid.Element>
              <FormGrid.Element>
                <Form.Input
                  className="w-full"
                  data-testid="invite-user-email"
                  disabled={formState.isSubmitting}
                  label={t('personalInfo.email')}
                  loading={users.loading}
                  name="email"
                  placeholder={t('personalInfo.email')}
                >
                  <Input.Inline.Icon align="left" name="MailSolid" />
                </Form.Input>
              </FormGrid.Element>
              <FormGrid.Element id="roleDropdown">
                <span>
                  <UsersForms.Dropdown.UserRoles
                    data-testid="invite-user-role"
                    disabled={formState.isSubmitting}
                    label={t('personalInfo.role')}
                    onChange={handleRoleChange}
                    placeholder={t('personalInfo.role')}
                  />
                  <RoleNote groupType={watch('userRole')} />
                </span>
              </FormGrid.Element>
            </FormGrid>
          </Card.Content>
        </Card>

        <div className="mt-6 flex justify-end">
          <Form.SubmitButton data-testid="invite-user-submit" variant="primary">
            {t('inviteUser.sendInvite')}
          </Form.SubmitButton>
        </div>
      </Form>
    </GraphQLErrorBoundary>
  )
}
